<template style="z-index: 100;">
  <v-layout column>
    <v-flex>
      <app-google-translator />
    </v-flex>
    <v-flex >
      <text-block
        title="Ticket Summary"
      />
      <the-table
        type="pcw"
        :quotedTickets="quotedTickets"
      />
      <v-divider v-if="quotedTickets.length" class="tableDivider" />
      <v-flex v-if="quotedTickets.length" mx-3 pt-2>
        <span class="totalCostWrapper">
          Your Protection Quote Total:
          <span class="totalCostText">{{totalCost}}</span>
        </span>
      </v-flex>
    </v-flex>
    <v-flex>
      <div class="billingTextBlockWrapper">
        <text-block
          title="Billing Information"
        />
      </div>
      <the-billing
        :first-name.sync="firstName"
        :last-name.sync="lastName"
        :number.sync="number"
        :expire-month.sync="expireMM"
        :expire-year.sync="expireYY"
        :cvv.sync="cvv"
        billingTitle="Card Information"
        billingDescription="Please complete all the fields below."
        :validate-form.sync="validateForm"
        :is-complete.sync="cardIsValid"
      />
    </v-flex>
    <v-flex>
      <the-underwriter-text
        :state="state"
      />
    </v-flex>
    <v-flex pb-4 pl-1 mt-4>
      <the-button
        :isLoading="orderLoading"
        :disabled="!cardIsValid"
        :buttonCallback="createOrder"
        buttonText="Submit Purchase"
        testId="submit-button"/>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import urlUtils from 'utils/url';

import TextBlock from 'components/TextBlock.vue';
import TheTable from 'components/TheTable.vue';
import TheBilling from 'components/TheBilling.vue';
import TheButton from 'components/TheButton.vue';
import TheUnderwriterText from 'components/TheUnderwriterText.vue';
import AppGoogleTranslator from 'components/AppGoogleTranslator.vue';
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'billing',
  components: {
    TextBlock,
    TheTable,
    TheBilling,
    TheButton,
    AppGoogleTranslator,
    TheUnderwriterText,
  },
  data: () => ({
    firstName: '',
    lastName: '',
    state: '',
    totalCost: 0,
    validateForm: false,
    number: '',
    cvv: '',
    quotedTickets: [],
    tickets: [],
    count: 0,
    cardIsValid: false,
    expireMM: '',
    expireYY: '',
    token: '',
    pcwRoute: '',
    apiKey: '',
    orderLoading: false,
  }),
  mounted() {
    if (this.userInformation.firstName && this.userInformation.lastName) {
      this.firstName = this.userInformation.firstName.charAt(0).toUpperCase()
        + this.userInformation.firstName.slice(1);
      this.lastName = this.userInformation.lastName.charAt(0).toUpperCase()
        + this.userInformation.lastName.slice(1);
      this.state = this.userInformation.state;
      this.apiKey = this.userInformation.apiKey;
      this.token = this.userInformation.token;
    }
    this.quotedTickets = this.createdFormattedTickets();
    this.createTotalCost(this.quotedTickets);
  },
  computed: {
    ...mapGetters('quote', ['globalQuotedTickets', 'globalQuotedTicketsUpdateCount', 'userInformation', 'internalQuoteCurrency']),
    ...mapGetters('order', ['openEndedOrderLoading']),
    ...mapGetters('widgetData', ['widgetData']),
    email() {
      const productFromUrl = urlUtils.getProductFromDomain();
      return productFromUrl !== 'enhancedrefundprogram' ? 'help@protecht.com' : 'help@fanshield.com';
    },
  },
  watch: {
    globalQuotedTicketsUpdateCount(value) {
      if (value) {
        this.quotedTickets = this.createdFormattedTickets();
        this.createTotalCost(this.quotedTickets);
      } else {
        this.quotedTickets = [];
      }
    },
  },
  methods: {
    ...mapActions(['pushMessageAlert']),
    ...mapActions('order', ['createOpenEndedOrder']),
    createdFormattedTickets() {
      const tickets = [];
      const modifiedGlobalQuotedTickets = Object.entries(this.globalQuotedTickets);

      for (let i = 0; i < modifiedGlobalQuotedTickets.length; i++) { // eslint-disable-line
        tickets.push(modifiedGlobalQuotedTickets[i][1]);
      }

      return tickets.flat();
    },
    createTotalCost(tickets) {
      this.totalCost = 0;
      if (tickets.length) {
        for (let i = 0; i < tickets.length; i++) { // eslint-disable-line
          this.totalCost = this.totalCost + (
            Number(tickets[i].ticketProtectionCost) * Number(tickets[i].quantity)
          );
        }
        this.totalCost = `${tickets[0].currency} ${this.totalCost.toFixed(2)}`;
      }
    },
    createEventForOrder() {
      const { eventName } = this.userInformation;
      const events = [{
        name: eventName,
        items: [],
      }];

      for (let i = 0; i < this.quotedTickets.length; i++) { // eslint-disable-line
        events[0].items.push({
          cost: this.quotedTickets[i].ticketCost,
          name: this.quotedTickets[i].ticketDescription,
          reference_number: uuidv4(),
        });
      }

      return events;
    },
    createOrder() {
      const {
        email,
        country,
        state,
        zipCode,
        address,
        city,
      } = this.userInformation;
      const locale = window.navigator.language.replace(/-/g, '_');

      const body = {
        pcw_token: this.userInformation.pcw_token,
        quote_token: this.quotedTickets[0].token,
        reference_id: 1,
        currency: this.internalQuoteCurrency || 'USD',
        customer: {
          first_name: this.firstName,
          last_name: this.lastName,
          email,
          // phone: 'string',
          locale: locale || 'en_US',
          address_1: address,
          city,
          state,
          postal_code: zipCode,
          country,
          // address_2: 'string',
        },
        payment: {
          number: this.number,
          expire_month: this.expireMM,
          expire_year: this.expireYY,
          cvv: this.cvv,
          type: 'card',
          // transaction_id: 'string',
          // token: 'string',
          // processor: 'string',
          // name: 'string',
        },
        items: this.quotedTickets.map((ticket, index) => ({
          event_id: 1,
          reference_id: index,
          name: this.userInformation.eventName,
          description: ticket.ticketDescription,
          unit_cost: ticket.ticketCost,
          quantity: ticket.quantity,
          // image_url: 'string',
          // reference_url: 'string',
        })),
        details: {
          events: [
            {
              event_id: 1,
              reference_id: 1,
              name: this.userInformation.eventName,
              start_date: new Date(this.quotedTickets[0].eventDate),
              // end_date: 'date',
              location: {
                venue: this.userInformation.eventVenue,
                city: this.userInformation.eventCity,
                state: this.userInformation.eventState,
                country: this.userInformation.eventCountry,
              },
            },
          ],
        },
      };

      const { apiKey } = this;
      this.orderLoading = true;

      this.createOpenEndedOrder({
        body,
        apiKey,
      })
        .then(() => {
          this.$router.push({ name: 'confirmation' });
          this.orderLoading = false;
        })
        .catch(() => {
          this.orderLoading = false;
          const pushMessage = `It looks like something while processing your card, please try again or email us for help at ${this.email}`;
          this.pushMessageAlert({
            message: pushMessage,
            type: 'error',
          });
          if ('parentIFrame' in window) {
            window.parentIFrame.sendMessage({ error: pushMessage });
          }
          document.body.scrollTop = document.documentElement.scrollTop = 0; // eslint-disable-line
        });

      setTimeout(() => this.orderLoading = false, 10000); // eslint-disable-line
    },
  },
};
</script>

<style scoped>
.underTableText {
  font-size: 16px;
  font-family: Gotham;
  text-align: right;
  color: #333333;
  padding-top: 5px;
  position: relative;
  right: 26px;
}

.openEndedDescription {
  padding-bottom: 0px !important;
}

.totalCoverageText {
  font-weight: bold;
}

.totalCostWrapper {
  font-family: Gotham;
  font-size: 20px;
  float: right;
  position: relative;
  right: 10px;
}

.totalCostText {
  font-weight: bold;
}

.bg-gray {
  background-color: #f2f2f2;
}

.link {
  text-decoration: none;
  margin: 24px 24px 36px 20px;
}

.text:hover {
  text-decoration: underline;
}

.totalCostText {
  font-weight: bold;
}

.tableDivider {
  border-width: 1.5px;
  max-width: 96%;
  margin: 0 auto;
}

.nextIcon {
  margin: 0px;
  position: relative;
  left: 50px;
  font-size: 16px;
}

.billingTextBlockWrapper {
  position: relative;
  top: 35px;
}

/* Tablet Break Point */
@media screen and (max-width: 1264px) {
}

/* Mid Phone to Tablet Break Point */
@media screen and (min-width: 767px) and (max-width: 959px) {
  .underTableText {
    right: 18px;
  }
}

/* Mobile Break Point */
@media screen and (max-width: 767px) {
  .underTableText {
    position: static;
    padding-right: 20px;
  }
}
</style>
